import { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useLang, useT } from "../../../../i18n";
import "./DateRangeModal.css";

function DateRangeModal({ setIsOpen, onChange, start, end, maxDate }) {
  const t = useT();
  const [lang] = useLang();
  const dateLocale = lang === "en" ? "en-US" : "ko-KR";
  const titleId = useId();
  const lastActiveElRef = useRef(null);
  const closeBtnRef = useRef(null);

  useEffect(() => {
    lastActiveElRef.current =
      typeof document !== "undefined" ? document.activeElement : null;

    // Move focus into the dialog for keyboard users.
    if (closeBtnRef.current) {
      closeBtnRef.current.focus();
    }

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);

      // Restore focus to the element that opened the dialog.
      const el = lastActiveElRef.current;
      if (el && typeof el.focus === "function") {
        el.focus();
      }
    };
  }, [setIsOpen]);
  // 자정/하루끝 보정
  const toStartOfDay = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };
  const toEndOfDay = (d) => {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x;
  };

  const todayStart = useMemo(() => toStartOfDay(new Date()), []);

  // ✅ 종료일 상한: 오늘(기본)
  const maxSelectableDate = useMemo(() => {
    return maxDate ? toEndOfDay(new Date(maxDate)) : toEndOfDay(new Date());
  }, [maxDate]);

  // 초기 범위: props 있으면 사용, 없으면 “오늘 기준 최근 7일”
  const initialRange = useMemo(() => {
    if (start && end)
      return { startDate: new Date(start), endDate: new Date(end) };
    const today = toStartOfDay(new Date());
    const start7 = new Date(today);
    start7.setDate(start7.getDate() - 6);
    return { startDate: start7, endDate: today };
  }, [start, end]);

  const [startDate, setStartDate] = useState(initialRange.startDate);
  const [endDate, setEndDate] = useState(initialRange.endDate);

  // ✅ 오늘을 "시작일"로 선택하는 것을 금지
  const isSameDay = (a, b) =>
    !!a && !!b && toStartOfDay(a).getTime() === toStartOfDay(b).getTime();

  // 범위 선택
  const handleRangeChange = (dates) => {
    const [s, e] = dates ?? [];

    // 케이스 A: 첫 클릭(시작 선택)이 '오늘'이면 무시
    if (s && !endDate && !startDate && isSameDay(s, todayStart)) {
      return; // 아무 것도 선택하지 않은 상태 유지
    }

    // 케이스 B: 기존에 시작일이 있었는데 다시 오늘로 바꾸려는 경우도 막기
    if (s && !e && isSameDay(s, todayStart)) {
      // 기존 시작일 유지 (또는 null 로 초기화하고 싶으면 setStartDate(null))
      return;
    }

    // 종료일 상한: 오늘까지만 허용
    if (e && toStartOfDay(e) > toStartOfDay(maxSelectableDate)) {
      setStartDate(s || startDate);
      setEndDate(maxSelectableDate);
      return;
    }

    setStartDate(s);
    setEndDate(e);
  };

  const handleApply = () => {
    if (startDate && endDate) {
      onChange(startDate, endDate);
      setIsOpen(false);
    }
  };

  const modal = (
    <div className="date-modal-overlay" onClick={() => setIsOpen(false)}>
      <div
        className="date-modal-content-range"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <button
          ref={closeBtnRef}
          type="button"
          className="date-modal-close"
          onClick={() => setIsOpen(false)}
          aria-label={t("data.dateModal.close")}
        >
          ×
        </button>
        <div className="date-modal-header">
          <span id={titleId}>{t("data.range.label")}</span>
        </div>

        <div className="date-modal-body-range">
          <div className="selected-range-display">
            <span className="range-date start">
              {startDate ? startDate.toLocaleDateString(dateLocale) : "-"}
            </span>
            ~
            <span className="range-date end">
              {endDate ? endDate.toLocaleDateString(dateLocale) : "-"}
            </span>
          </div>

          <DatePicker
            selected={startDate}
            onChange={handleRangeChange}
            startDate={startDate}
            endDate={endDate}
            maxDate={new Date()}
            selectsRange
            inline
            monthsShown={1}
            calendarClassName="range-picker-calendar"
            dateFormat="yyyy-MM-dd"
            // ✅ 시작일은 제한 없음(minDate 미설정)
            // ✅ 종료일 상한만 적용
            filterDate={(d) => {
              // 종료일만 상한 체크(시작일은 통과)
              // react-datepicker는 날짜 하나씩 필터하므로 "오늘 이후" 자체를 비표시
              return toStartOfDay(d) <= toStartOfDay(maxSelectableDate);
            }}
          />
        </div>

        <div className="date-modal-footer">
          <button className="date-btn-cancel" onClick={() => setIsOpen(false)}>
            {t("data.dateModal.cancel")}
          </button>
          <button
            className="date-btn-apply"
            onClick={handleApply}
            disabled={!startDate || !endDate}
          >
            {t("data.dateModal.apply")}
          </button>
        </div>
      </div>
    </div>
  );

  return typeof document === "undefined"
    ? modal
    : createPortal(modal, document.body);
}

export default DateRangeModal;
